import { computed, nextTick, ref, watch, type Ref } from 'vue';
import dayjs from '~/utils/dayjs';
import { formatTz, getClientTz } from '~/utils/date';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useGroupSendingCalendar } from './useGroupSendingCalendar';
import { useGroupMessageRules } from './useGroupMessageRules';

interface UseQuickBlastWhenOptions {
    campaignType: Ref<'marketing' | 'transactional' | undefined>;
    modelValue: Ref<Date | string | undefined>;
    currentTab?: Ref<'now' | 'later'>;
}

interface UseQuickBlastWhenReturn {
    date: Ref<Date | string | undefined>;
    canScheduleNow: Ref<boolean>;
    initialCalendarLoading: Ref<boolean>;
    dateFormatted: Ref<string | null>;
    tzFormatted: Ref<string>;
    dateWithTzFormatted: Ref<string | null>;
    campaignTypeRestricted: Ref<boolean>;
    calculateCanScheduleNow: () => boolean;
    loadCurrentMonthCalendar: () => Promise<void>;
    roundDateToNearestFiveMinutes: (date?: string) => void;
}

export function useQuickBlastWhen(
    options: UseQuickBlastWhenOptions,
    emit?: (event: 'update:modelValue', value: Date | string | undefined) => void
): UseQuickBlastWhenReturn {
    const { currentGroup } = useCurrentGroup();
    const { isTypeRestricted } = useGroupMessageRules();
    const { getSendingCalendar, isDateTimeAllowed, disabledDates, isMonthLoaded, findClosestAvailableTime, clearData } =
        useGroupSendingCalendar();

    const { campaignType, modelValue, currentTab } = options;

    const date = ref<Date | string | undefined>(modelValue.value);

    const campaignTypeRestricted = computed(() => (campaignType.value ? isTypeRestricted(campaignType.value) : false));
    const clientTz = computed(() => getClientTz());

    const initialCalendarLoading = ref(false);
    const canScheduleNow = ref(!currentGroup.value?.group_id || !campaignTypeRestricted.value);

    const calculateCanScheduleNow = (): boolean => {
        if (!currentGroup.value?.group_id || !campaignTypeRestricted.value) {
            return true;
        }

        const now = dayjs().tz(clientTz.value);
        const currentMonth = { year: now.year(), month: now.month() + 1 };

        if (!isMonthLoaded(currentMonth)) {
            return false;
        }

        const dateKey = now.format('YYYY-MM-DD');
        const isDateDisabled = disabledDates.value.some(
            (disabledDate) => dayjs(disabledDate).tz(clientTz.value).format('YYYY-MM-DD') === dateKey
        );

        if (isDateDisabled) {
            return false;
        }

        const currentMinutes = now.minute();
        const roundedMinutes = Math.ceil(currentMinutes / 5) * 5;

        const roundedTime =
            roundedMinutes >= 60 ? now.add(1, 'hour').minute(0).second(0) : now.minute(roundedMinutes).second(0);

        return isDateTimeAllowed(roundedTime.toDate());
    };

    const dateFormatted = computed(() =>
        date.value && date.value !== 'now'
            ? dayjs(date.value).tz(clientTz.value).format('MMM DD, YYYY [at] h:mm a')
            : null
    );
    const tzFormatted = computed(() => formatTz(dayjs().tz(clientTz.value).format('z')));
    const dateWithTzFormatted = computed(() =>
        date.value && date.value !== 'now' ? dateFormatted.value + ' ' + tzFormatted.value : null
    );

    const loadCurrentMonthCalendar = async () => {
        if (!currentGroup.value?.group_id || !campaignType.value || !campaignTypeRestricted.value) {
            return;
        }

        initialCalendarLoading.value = true;
        const now = dayjs().tz(clientTz.value);
        const activeMonth = { year: now.year(), month: now.month() + 1 };
        await getSendingCalendar(currentGroup.value.group_id, campaignType.value, activeMonth);

        canScheduleNow.value = calculateCanScheduleNow();
        await nextTick();
        initialCalendarLoading.value = false;
    };

    const roundDateToNearestFiveMinutes = (dateToTransform?: string) => {
        const transformedDate = dateToTransform
            ? dayjs(dateToTransform).tz(clientTz.value)
            : dayjs().tz(clientTz.value);
        const minutes = transformedDate.minute();
        const roundedMinutes = minutes >= 56 ? Math.floor(minutes / 5) * 5 : Math.ceil(minutes / 5) * 5;

        date.value = transformedDate.minute(roundedMinutes).toDate();
    };

    watch(campaignType, () => {
        clearData();
        loadCurrentMonthCalendar();
    });

    watch(
        () => modelValue.value,
        (newModelValue) => {
            date.value = newModelValue;
        },
        { immediate: true }
    );

    if (emit) {
        watch(date, (newDate) => emit('update:modelValue', newDate));
    }

    if (currentTab) {
        watch(currentTab, (newCurrentTab, oldCurrentTab) => {
            if (newCurrentTab === 'now') {
                date.value = 'now';
            } else if (newCurrentTab === 'later') {
                if (currentGroup.value?.group_id && campaignTypeRestricted.value) {
                    if (oldCurrentTab === 'now' && canScheduleNow.value) {
                        date.value = findClosestAvailableTime();
                    } else {
                        date.value = undefined;
                    }
                } else {
                    const now = dayjs().tz(clientTz.value);
                    const minutes = now.minute();
                    const roundedMinutes = Math.ceil(minutes / 5) * 5;
                    date.value = now.minute(roundedMinutes).toDate();
                }
            }
        });
    }

    return {
        date,
        canScheduleNow,
        initialCalendarLoading,
        dateFormatted,
        tzFormatted,
        dateWithTzFormatted,
        campaignTypeRestricted,
        calculateCanScheduleNow,
        loadCurrentMonthCalendar,
        roundDateToNearestFiveMinutes,
    };
}
